import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ErrorPage, useAuth, useThread } from '@screentone/addon-auth-wrapper';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { AllessehContentUpdateProvider } from 'contexts/allesseh-content-update/AllessehContentUpdateContext';
import { ContentIdTrackingProvider } from 'contexts/content-id-tracking/ContentIdTrackingContext';
import { ArticlePromotionLog, DataModelProvider } from 'contexts/datamodel/DataModelContext';
import { DragAndDropProvider } from 'contexts/drag-and-drop/DragAndDropContext';
import { PageLockProvider } from 'contexts/page-lock/PageLockContext';
import { PagePublishProvider, PageUpdateType } from 'contexts/page-publish-dto/PagePublishContext';
import { PageStructureProvider } from 'contexts/page-structure/PageStructureContext';
import { PageTypeSettingsProvider } from 'contexts/page-type-settings/PageTypeSettingsContext';
import { SummarianProvider } from 'contexts/summarian/SummarianContext';
import { TrashProvider } from 'contexts/trash/TrashContext';
import { AvailableFeatureFlags, PageDto, PageType, usePageDtoByAllessehIdQuery } from 'data/generated/graphql';
import { GraphqlError } from 'data/graphqlClient';
import { ScratchpadProvider } from 'features/section-page-edit/components/scratch-pad/scratchpad-context';
import { SectionPageEdit } from 'features/section-page-edit/SectionPageEdit';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useRequirePublisher } from 'hooks/roles';
import { useCommandFSearch } from 'hooks/useCommandFSearch';
import { PageDTOSubscriptionTopic, usePageDTOSubscription } from 'hooks/usePageDTOSubscription';
import { PAGE_UPDATE_THREAD_PREFIX } from 'utils/constants';
import { reportError } from 'utils/errorBoundary';
import { getLayoutIdFromPageRoot } from 'utils/layoutUtils';
import { getUserId } from 'utils/user';
import SectionPageVersionFallback from './SectionPageVersionFallback';
import { useSectionPageGroups } from './useSectionPageGroups';

const isAllessehId = (allessehIdOrSlug: string) => /^UCS(D|P)_/.test(allessehIdOrSlug);

type LocationState = { historicalPageDto?: PageDto } | null;

type DraftUpdateMessagePayload = {
  message: PageUpdateType;
  triggeredBy: {
    id: string;
    name: string;
  };
  takenOverBy?: {
    id: string;
    name: string;
  };
};

const SectionPageEditPage = () => {
  useRequirePublisher();
  const [data, setData] = useState<PageDto | null>(null);

  const { allessehIdOrSlug, publishUtc: versionUtcParam } = useParams();
  const versionPublishUtc = versionUtcParam ? Number(versionUtcParam) : undefined;
  const location = useLocation();
  const { data: publicationSettings, isLoading: isPublicationSettingsLoading } = usePublicationSettings();
  const currentProperty = useConvertedProperty();
  const allessehId = useRef(allessehIdOrSlug);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { threadMessages: draftUpdatedMessage } = useThread({
    threadId: `${PAGE_UPDATE_THREAD_PREFIX}${allessehId.current}`,
    limit: 1
  });
  const { alertWarning, alertError } = useAlert();
  const mountTimestamp = useRef(Date.now());
  const forceResetOnNextFetch = useRef(false);
  if (allessehIdOrSlug && !isAllessehId(allessehIdOrSlug)) {
    const slug = allessehIdOrSlug;
    allessehId.current = publicationSettings?.publicationSetting.slugMappings?.[slug] as string;
  }

  const {
    data: pageDTOResult,
    isLoading: isPageDTOLoading,
    isFetching: isPageDTORefetching,
    error: loadingError,
    isRefetching,
    refetch: refetchPageDTO
  } = usePageDtoByAllessehIdQuery(
    { allessehId: allessehId.current ?? '', publicationKey: currentProperty ?? '' },
    { enabled: !!allessehId.current && !!currentProperty }
  );

  const activePageData = data ?? (pageDTOResult?.pageDTOByAllessehId as PageDto | undefined);
  const { effectiveStructure, hydratedRoot } = useSectionPageGroups(activePageData);
  const historicalPageDto = versionPublishUtc ? ((location.state as LocationState)?.historicalPageDto ?? null) : null;

  // On claim and release, reconcile with server
  const handleForceRefetch = useCallback(() => {
    forceResetOnNextFetch.current = true;
    refetchPageDTO().catch(console.error);
  }, [refetchPageDTO]);

  const handlePageDeleted = useCallback(() => {
    const pageName = pageDTOResult?.pageDTOByAllessehId.metadata.name;
    const message = pageName
      ? `The page "${pageName}" has been deleted by another user.`
      : 'This page has been deleted by another user.';
    alertError(message);
    navigate(`/${currentProperty}/`);
    // alertError and navigate are stable references but not memoized, excluding them keeps this callback stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProperty, pageDTOResult]);

  const pageDeletedSubscriptionParams = useMemo(
    () => ({
      allessehId: allessehId.current ?? '',
      publicationKey: currentProperty ?? '',
      currentUserId: user?.email ?? '',
      onDeleted: handlePageDeleted,
      topic: 'pageDeleted' as PageDTOSubscriptionTopic
    }),
    [currentProperty, user, handlePageDeleted]
  );

  usePageDTOSubscription(pageDeletedSubscriptionParams);

  useEffect(() => {
    if (pageDTOResult && !isRefetching) {
      const pageDto = pageDTOResult.pageDTOByAllessehId as PageDto;
      if (forceResetOnNextFetch.current) {
        forceResetOnNextFetch.current = false;
        // Spread root into a new reference to bypass React Query structural sharing.
        // Without this, when the refetched root content equals the originally fetched root,
        // React Query returns the same object reference, so DataModelContext's processedRoot
        // useMemo never recomputes and the internal root state is never reset.
        setData({ ...pageDto, root: { ...pageDto.root } as PageDto['root'] });
      } else {
        setData(pageDto);
      }
    }
  }, [pageDTOResult, isRefetching]);

  useEffect(() => {
    if (draftUpdatedMessage.length > 0 && user) {
      const [latestMessage] = draftUpdatedMessage;
      const payload = latestMessage.payload as DraftUpdateMessagePayload;
      const userId = getUserId(user);

      // Only display the alert if the message is new
      if (userId && latestMessage.timestamp > mountTimestamp.current) {
        if (payload.takenOverBy?.id === userId) {
          handleForceRefetch();
        } else if (payload.triggeredBy.id !== userId) {
          let messageType: string | null = null;

          switch (payload.message) {
            case PageUpdateType.Draft:
              messageType = 'saved a draft of';
              break;
            case PageUpdateType.Publish:
              messageType = 'published';
              break;
            default:
              break;
          }

          if (messageType) {
            alertWarning(`${payload.triggeredBy.name} has ${messageType} a new version of the page.`, {
              link: {
                text: 'Refresh to see changes.',
                url: window.location.href
              }
            });
          } else {
            console.warn('Received unknown page update message type:', payload.message);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftUpdatedMessage, handleForceRefetch, user]);

  useEffect(
    () => () => {
      setData(null);
    },
    []
  );

  // Enables article cards to be searched for using their Allesseh IDs via "command + f"
  useCommandFSearch();

  if (loadingError) {
    reportError('Error fetching section page data: ', loadingError as Error);
    if (
      loadingError instanceof GraphqlError ||
      (loadingError instanceof Error && loadingError.message === 'Failed to fetch')
    ) {
      return <ErrorPage type="503" />;
    }
    if (loadingError instanceof Error && loadingError.message === 'Not found') {
      return <ErrorPage type="404" />;
    }
    if (!historicalPageDto) {
      return (
        <SectionPageVersionFallback
          allessehId={allessehId.current}
          currentProperty={currentProperty}
          draftError={loadingError}
        />
      );
    }
    // historicalPageDto passed via navigation state — fall through and render with historical data
  }

  const pageDTO = (pageDTOResult?.pageDTOByAllessehId ?? historicalPageDto) as PageDto;

  return (
    <>
      {isPublicationSettingsLoading || isPageDTOLoading ? (
        <ResponsiveLoader />
      ) : (
        <AllessehContentUpdateProvider>
          <SummarianProvider>
            <DataModelProvider
              type="PageDTO"
              root={hydratedRoot ?? data?.root ?? pageDTO.root}
              metadata={data?.metadata ?? pageDTO.metadata}
              // When restoring from a historical version, the GraphQL selection set only includes
              // type/metadata/root — articlePromotionLog and scratchpad are absent. We default to
              // empty values rather than extending the query because this data belongs to the old
              // draft, not the version being recovered; the user saves a fresh draft from here.
              articlePromotionLog={
                (data?.articlePromotionLog ?? pageDTO.articlePromotionLog ?? null) as ArticlePromotionLog
              }
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              validations={data?.validations ?? pageDTO.validations ?? undefined}
              structure={effectiveStructure}
            >
              <ScratchpadProvider scratchpad={data?.scratchpad ?? pageDTO.scratchpad ?? null}>
                <ContentIdTrackingProvider>
                  <PageTypeSettingsProvider pageType={pageDTO.root.attributes.pageType as PageType}>
                    <PageStructureProvider layoutId={getLayoutIdFromPageRoot(data?.root ?? pageDTO.root)}>
                      <PagePublishProvider setData={setData}>
                        <TrashProvider>
                          <DragAndDropProvider>
                            <PageLockProvider
                              allessehId={allessehId.current ?? ''}
                              fragments={['publishing']}
                              featureFlag={AvailableFeatureFlags.PageLockingEnabled}
                              refetcher={handleForceRefetch}
                            >
                              <SectionPageEdit isRefetching={isPageDTORefetching} />
                            </PageLockProvider>
                          </DragAndDropProvider>
                        </TrashProvider>
                      </PagePublishProvider>
                    </PageStructureProvider>
                  </PageTypeSettingsProvider>
                </ContentIdTrackingProvider>
              </ScratchpadProvider>
            </DataModelProvider>
          </SummarianProvider>
        </AllessehContentUpdateProvider>
      )}
    </>
  );
};

export default SectionPageEditPage;
